import {
  DEFAULT_COLORMAP_ID,
  colormapStops,
  getColormap,
  sampleRamp,
  type RampStop,
} from 'components/colormaps';
import type { DefaultColorRamp, SubsurfaceMapRow } from './types';

/** Painted map opacity (0–100) before the user changes it (the mock's 80%).
 *  Percent, the unit the colour modal's Transparency works in; only the Mapbox
 *  paint property divides by 100, since it takes a 0–1 fraction. */
export const DEFAULT_RASTER_OPACITY_PERCENT = 80;

/** The shown map's cells: its Map Query source and fill layer ids, and the tile
 *  layer the cells tiles carry (Map::Tiles::SubsurfaceMapCells::LAYER_NAME). */
export const CELLS_SOURCE_ID = 'subsurface-map-cells';
export const CELLS_LAYER_ID = 'subsurface-map-cells-fill';
export const CELLS_SOURCE_LAYER = 'cells';
/** Past this zoom a grid's cells are drawn at full size anyway, so deeper zooms
 *  reuse these tiles (overzoom) instead of requesting more. */
export const CELLS_SOURCE_MAX_ZOOM = 14;

/** Handles a named ramp opens with in the editor, evenly spaced (the mock's five). */
const DEFAULT_STOP_COUNT = 5;

/**
 * The ramp stops a map starts from: its stored default (custom stops win over the
 * named ramp) or Viridis. A named ramp is sampled at DEFAULT_STOP_COUNT positions,
 * so the editor opens with a handful of handles.
 * @param {DefaultColorRamp | null} ramp The map's default ramp from the backend.
 * @return {RampStop[]} Stops from low to high.
 */
export function defaultRampStops(ramp: DefaultColorRamp | null): RampStop[] {
  if (ramp?.stops?.length) return ramp.stops;
  const full = colormapStops(
    getColormap(ramp?.rampId ?? DEFAULT_COLORMAP_ID).id,
    ramp?.reversed ?? false
  );
  return Array.from({ length: DEFAULT_STOP_COUNT }, (_, index) => {
    const offset = (index / (DEFAULT_STOP_COUNT - 1)) * 100;
    return { offset, color: sampleRamp(full, offset) };
  });
}

/**
 * The lowest and highest values across the listed maps: the backend's pooled
 * histogram range when it has one, else the rows' own min/max.
 * @param {SubsurfaceMapRow[]} maps The listed maps.
 * @param {{ min: number, max: number } | undefined} histogram The pooled facet.
 * @return {[number, number]} [min, max]; [0, 0] when nothing has stats.
 */
export function pooledRangeOf(
  maps: SubsurfaceMapRow[],
  histogram: { min: number; max: number } | undefined
): [number, number] {
  if (histogram) return [histogram.min, histogram.max];
  const mins: number[] = [];
  const maxes: number[] = [];
  maps.forEach((map) => {
    if (map.valueMin !== null) mins.push(map.valueMin);
    if (map.valueMax !== null) maxes.push(map.valueMax);
  });
  return mins.length ? [Math.min(...mins), Math.max(...maxes)] : [0, 0];
}
